import "../css/EmailTemplate.css";
import { useAtom } from "jotai";
import {
  clinicAtom,
  doctorSettingAtom,
} from "../../_helper/constant/constant-atom";
import { useReactToPrint } from "react-to-print";
import { IoCloseSharp, IoPrintSharp } from "react-icons/io5";
import { Fragment, useEffect, useRef, useState } from "react";
import { pageSizes } from "../../doctor_setup/constants/_constant";
import { useItemsFilter } from "../../_helper/actions/app/items";
import moment from "moment";
import { getImageURL } from "../../_helper/utils/image";
import { tempIPDPatientDetailsAtom } from "../constants/_constant";
import { calculateAge } from "../../_helper/utils/utils";

const EmailTemplate = ({
  invoiceData: invoice_data,
  closeModal,
  width = "",
  isPrint,
}) => {
  const [invoiceData, setInvoiceData] = useState(null);
  const [tempIPDPatientDetails] = useAtom(tempIPDPatientDetailsAtom);
  const [selectedPage, setSelectedPage] = useState("A5");
  const [doctorSettings] = useAtom(doctorSettingAtom);
  const [clinic] = useAtom(clinicAtom);
  const billPrintRef = useRef(null);
  const billType = invoiceData?.bill_type;
  const patienName = invoiceData?.patient_details?.full_name;
  const billNo = invoiceData?.invoice_no;
  const bill_type = {
    IPD: invoiceData?.admission_details?.IPD_No,
    OPD: invoice_data?.opd?.opd_number,
  };
  const DOA = `${moment(invoiceData?.admission_details?.admission_date).format(
    "DD-MMM-YYYY"
  )} at ${invoiceData?.admission_details?.admission_time}`;
  const DOD = `${moment(invoiceData?.admission_details?.discharge_date).format(
    "DD-MMM-YYYY"
  )} at ${invoiceData?.admission_details?.discharge_time}`;
  const opdTime = invoice_data?.opd?.date;
  const amountInWords = invoiceData?.amount_in_word;
  const invoiceDate = moment(invoiceData?.invoice_date).format("DD-MMM-YYYY");
  const injury_no = invoiceData?.admission_details?.injury_no;
  const customerName =
    invoice_data?.institution_customer?.institution_name || "";
  const diagnosis = invoice_data?.admission_details?.diagnosis;
  const treatment = invoice_data?.admission_details?.treatment;
  const inchargeDoctor = tempIPDPatientDetails?.inchargeDoctor;
  const gender = tempIPDPatientDetails.gender;
  const dob = tempIPDPatientDetails.dob;
  const age = tempIPDPatientDetails.dob
    ? calculateAge(tempIPDPatientDetails.dob).years
    : tempIPDPatientDetails.age;
  const uhid = tempIPDPatientDetails.uhid;
  const paymode = tempIPDPatientDetails.payMode;
  const address = tempIPDPatientDetails.address;

  // New fields added in Customer Info:
  const tpa = tempIPDPatientDetails.tpa;
  const insuranceCompany = tempIPDPatientDetails.insuranceCompany;

  const { data } = useItemsFilter(
    "payments/billing/",
    `?action=getcreatePDFInvoice&clinic_id=${clinic?.id}&backend=True&invoice_data=${invoice_data?.id}`,
    clinic?.id && invoice_data?.id ? true : false
  );

  console.log("details", data);

  // Define the print function using the useReactToPrint hook
  const handlePrint = useReactToPrint({
    content: () => billPrintRef.current,
  });

  useEffect(() => {
    if (isPrint && invoiceData) {
      setTimeout(() => {
        handlePrint(); // Print after modal loads
        closeModal();
      }, 500); // Delay to let modal render before triggering print
    }
  }, [isPrint, invoiceData]);

  useEffect(() => {
    if (data?.invoiceData) {
      setInvoiceData(data?.invoiceData);
    }
  }, [data]);

  // console.log(invoiceData);

  return (
    <div className="opacity-0">
      {/* Inline print styles – these can also be moved to a dedicated print CSS file */}
      <style>
        {`
          @page {
            size: ${selectedPage};
            margin: 3mm;
          }
          .print-header {
            position: absolute; 
            left: 0;
            right: 0;
            width: 100%;
            z-index: 100;
            top: 0;
         
          }
          .cu-invoice-email-template {
            font-family: Arial, sans-serif;
            font-size: 14px;
            line-height: 1.5;
          }
          .cu-invoice-pdf-table table {
            width: 100%;
            border-collapse: collapse;
          }
          .cu-invoice-pdf-table th,
          .cu-invoice-pdf-table td {
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
          }
          .cu-invoice-pdf-table th {
            background-color: #f2f2f2;
          }
          .system-generated-notice {
            text-align: center;
            font-size: 12px;
            margin-top: 20px;
            color: #555;
          }
        `}
      </style>

      {/* Wrapper with a 3mm margin to enforce the spacing */}
      <div style={{ margin: "3mm" }}>
        <div ref={billPrintRef} className="cu-invoice-email-template">
          {doctorSettings?.header_footer_setup[selectedPage]?.headerHeight >
            0 && (
            <div className="print-header">
              <img
                src={getImageURL(
                  doctorSettings?.header_footer_setup[selectedPage]?.headerUrl
                )}
                className="w-full"
                alt="Header"
              />
            </div>
          )}

          <div className="cu-invoice-billing-type" style={{ marginTop: "" }}>
            {billType} Billing Summary
          </div>

          <div className="cu-invoice-patient-details">
            <div>
              {customerName && <div>{`Customer: ${customerName}`}</div>}
              <div>{`Patient Name: ${gender === "Male" ? "Mr." : "Mrs."} ${patienName}`}</div>
              {age && gender && <div>{`Age/Sex: ${age} / ${gender}`}</div>}
              <div>{`Bill No: ${billNo}`}</div>
              <div>{`Invoice Date: ${invoiceDate}`}</div>
              {uhid && <div>{`UHID: ${uhid}`}</div>}
              {billType === "IPD" && (
                <div>{`Incharge Doctor: ${inchargeDoctor}`}</div>
              )}
            </div>
            <div>
              {billType === "IPD" && <div>{`Address: ${address}`}</div>}
              <div>{`${billType} No: ${billNo}`}</div>
              {injury_no && <div>{`Injury Slip No: ${injury_no}`}</div>}
              {billType === "IPD" && <div>{`DOA: ${DOA}`}</div>}
              {billType === "IPD" && <div>{`DOD: ${DOD}`}</div>}
              {billType === "IPD" && <div>{`Pay Mode: ${paymode}`}</div>}
              {billType === "OPD" && opdTime !== "Invalid date" && (
                <div>{`OPD Date: ${opdTime}`}</div>
              )}
            </div>
          </div>

          <div className="cu-invoice-pdf-table">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Rate/Package</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData?.invoice_lines?.map((invcSer, pkgIdx) => {
                  if (invcSer?.line_package) {
                    return invcSer?.line_package?.services?.map(
                      (pkgSer, pkgSerIdx) => (
                        <tr
                          key={`${pkgSer?.clinic_services_id?.id}${pkgSerIdx}`}
                        >
                          <td>{pkgSer?.name_of_service}</td>
                          <td style={{ borderBottom: "none" }}>
                            {pkgSerIdx ===
                            Math.ceil(
                              invcSer?.line_package?.services?.length / 2
                            ) -
                              1
                              ? invcSer?.quantity || 0
                              : ""}
                          </td>
                          <td style={{ borderBottom: "none" }}>
                            {pkgSerIdx ===
                            Math.ceil(
                              invcSer?.line_package?.services?.length / 2
                            ) -
                              1
                              ? invcSer?.line_package?.name_of_package
                              : ""}
                          </td>
                          {pkgSerIdx ===
                            Math.ceil(
                              invcSer?.line_package?.services?.length / 2
                            ) -
                              1 && (
                            <td style={{ borderBottom: "none" }}>
                              {`Rs ${invcSer?.total}`}
                            </td>
                          )}
                        </tr>
                      )
                    );
                  } else if (
                    invcSer?.line_service?.name_of_service?.toUpperCase() ===
                      "MEDICINE CHARGES" ||
                    invcSer?.line_service?.name_of_service?.toUpperCase() ===
                      "MEDICINE CHARGE"
                  ) {
                    return (
                      <Fragment key={`${invcSer?.line_service?.id}${pkgIdx}`}>
                        <tr>
                          <td>
                            <b>{invcSer?.line_service?.name_of_service}</b>
                          </td>
                          <td>
                            <b>
                              {invcSer?.type === "Qty"
                                ? invcSer?.quantity
                                : "-"}
                            </b>
                          </td>
                          <td>
                            <b>
                              {Number(invcSer?.price) > 0
                                ? invcSer?.price
                                : "-"}
                            </b>
                          </td>
                          <td>
                            <b>{"-"}</b>
                          </td>
                        </tr>
                        {invcSer?.sub_lines?.map((subline, subIdx) => (
                          <tr className="text-[12px]" key={subIdx}>
                            <td>
                              {`${subline?.medicine_name} `}
                              {subline?.generic_name &&
                              subline?.generic_name !== "nan"
                                ? `(${subline?.generic_name})`
                                : ""}
                              {subline?.batch_no?.map((ele) =>
                                ele?.consumed_qty > 0 ? (
                                  <div
                                    className="text-[11.5px]"
                                    key={ele?.batchId}
                                  >{`Batch No: ${ele?.batchNo}, Qty: ${ele?.consumed_qty}, expiry: ${moment(
                                    ele?.expiry
                                  ).format("DD MMM YYYY")}`}</div>
                                ) : null
                              )}
                            </td>
                            <td>{subline?.quantity || 0}</td>
                            <td>{subline?.price}</td>
                            <td>{`Rs ${
                              Number(subline?.price) * Number(subline?.quantity)
                            }`}</td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  } else {
                    return (
                      <tr key={`${invcSer?.line_service?.id}${pkgIdx}`}>
                        <td>{invcSer?.line_service?.name_of_service}</td>
                        <td style={{ borderTop: "1px solid #000000cc" }}>
                          {invcSer?.type === "Qty" ? invcSer?.quantity : "-"}
                        </td>
                        <td style={{ borderTop: "1px solid #000000cc" }}>
                          {Number(invcSer?.price) > 0 ? invcSer?.price : "-"}
                        </td>
                        <td style={{ borderTop: "1px solid #000000cc" }}>
                          {`Rs ${
                            (Number(invcSer?.quantity) || 0) *
                            (Number(invcSer?.price) || 0)
                          }`}
                        </td>
                      </tr>
                    );
                  }
                })}
                <tr>
                  <td></td>
                  <td></td>
                  <td style={{ fontWeight: "700" }}>Total</td>
                  <td style={{ fontWeight: "700" }}>
                    {`Rs ${invoiceData?.invoice_total}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="text-[13px]">
              <b>Total Amount in words:</b>
              {` ${amountInWords} Only.`}
            </div>
            {diagnosis && (
              <div className="text-[13px]">
                <b>Diagnosis:</b>
                {` ${diagnosis}`}
              </div>
            )}
            {treatment && (
              <div className="text-[13px]">
                <b>Treatment:</b>
                {` ${treatment}`}
              </div>
            )}
            {tpa && (
              <div className="text-[13px]">
                <b>TPA/PANEL NAME:</b> {tpa || "-"}
              </div>
            )}
            {insuranceCompany && (
              <div className="text-[13px]">
                <b>INSURANCE COMPANY:</b> {insuranceCompany || "-"}
              </div>
            )}
          </div>

          {/* <div className="system-generated-notice">
            This is a system generated Invoice. No signature is required.
          </div> */}
          <div className="text-[13px] text-right mt-2">
            <b>Created By : SUPER ADMIN</b>
          </div>
          <div className="flex flex-col items-end overflow-hidden m-0 p-0">
            {doctorSettings?.header_footer_setup[selectedPage]
              ?.printSignature && (
              <div
                className="overflow-hidden m-0 p-0"
                style={{
                  height: `${doctorSettings?.header_footer_setup[selectedPage]?.signatureHeight}cm`,
                }}
              >
                <div
                  style={{
                    width: `${doctorSettings?.header_footer_setup[selectedPage]?.signatureWidth}cm`,
                  }}
                  className="m-0 p-0"
                >
                  <img
                    src={getImageURL(
                      doctorSettings?.header_footer_setup[selectedPage]
                        ?.signatureImg
                    )}
                    className="m-0 p-0"
                    alt="Signature"
                  />
                  {doctorSettings?.header_footer_setup[
                    selectedPage
                  ]?.signatureText
                    ?.split("\n")
                    .map((line, index) => (
                      <div key={index}>{line}</div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <div id="footer">
            {doctorSettings.header_footer_setup[selectedPage]?.footerHeight >
              0 && (
              <div id="footer" className="print-footer">
                <img
                  src={getImageURL(
                    doctorSettings.header_footer_setup[selectedPage]?.footerUrl
                  )}
                  alt="footer"
                  className="h-full w-full"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplate;
